import { DEFAULT_MULTIPLIERS, PLAY_TYPES } from './payouts';

export const MIN_PAYOUT_MULTIPLIER = 1;
export const MAX_PAYOUT_MULTIPLIER = 10_000;

export type PayoutMultipliers = {
  fijo: number;
  corrido: number;
  parle: number;
  centena: number;
};

const TYPES = [
  PLAY_TYPES.FIJO,
  PLAY_TYPES.CORRIDO,
  PLAY_TYPES.PARLE,
  PLAY_TYPES.CENTENA,
] as const;

/** Integer 1..10000, or null if missing/invalid. */
export function clampPayoutMultiplier(raw: unknown): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.floor(n);
  if (rounded < MIN_PAYOUT_MULTIPLIER || rounded > MAX_PAYOUT_MULTIPLIER) return null;
  return rounded;
}

/** House config multipliers with code defaults for missing types. */
export function resolveHousePayoutMultipliers(raw: unknown): PayoutMultipliers {
  const src = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  return {
    fijo: clampPayoutMultiplier(src.fijo) ?? DEFAULT_MULTIPLIERS.fijo,
    corrido: clampPayoutMultiplier(src.corrido) ?? DEFAULT_MULTIPLIERS.corrido,
    parle: clampPayoutMultiplier(src.parle) ?? DEFAULT_MULTIPLIERS.parle,
    centena: clampPayoutMultiplier(src.centena) ?? DEFAULT_MULTIPLIERS.centena,
  };
}

export function payoutMultiplierForType(
  multipliers: PayoutMultipliers,
  type: string,
): number {
  const key = String(type ?? '').toLowerCase();
  if (key === PLAY_TYPES.FIJO) return multipliers.fijo;
  if (key === PLAY_TYPES.CORRIDO) return multipliers.corrido;
  if (key === PLAY_TYPES.PARLE) return multipliers.parle;
  if (key === PLAY_TYPES.CENTENA) return multipliers.centena;
  return 0;
}

/** Persist only valid entries; omit empty so defaults stay implicit. */
export function sanitizePayoutMultipliersPatch(
  raw: unknown,
): Partial<PayoutMultipliers> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const src = raw as Record<string, unknown>;
  const out: Partial<PayoutMultipliers> = {};
  for (const type of TYPES) {
    const clamped = clampPayoutMultiplier(src[type]);
    if (clamped != null) out[type] = clamped;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
